import { useState } from "react";
import { Check, Copy } from "lucide-react";

/**
 * Reference of an instance's REST API (apps/api). Keep in sync with
 * apps/api/src/routes/* and apps/api/src/schemas/*.
 */

type Method = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
type Access = "public" | "user" | "admin";

interface Endpoint {
  method: Method;
  path: string;
  access: Access;
  summary: string;
  query?: Array<[string, string]>;
  body?: string;
  response?: string;
  note?: string;
}

interface Group {
  id: string;
  title: string;
  intro?: React.ReactNode;
  endpoints: Endpoint[];
}

const METHOD_STYLE: Record<Method, string> = {
  GET: "bg-emerald-50 text-emerald-700 ring-emerald-200",
  POST: "bg-indigo-50 text-indigo-700 ring-indigo-200",
  PUT: "bg-amber-50 text-amber-700 ring-amber-200",
  PATCH: "bg-amber-50 text-amber-700 ring-amber-200",
  DELETE: "bg-rose-50 text-rose-700 ring-rose-200",
};

const ACCESS_LABEL: Record<Access, string> = {
  public: "Public",
  user: "Connecté",
  admin: "Admin",
};

const ACCESS_STYLE: Record<Access, string> = {
  public: "bg-gray-100 text-gray-600",
  user: "bg-sky-50 text-sky-700",
  admin: "bg-violet-50 text-violet-700",
};

const BASE = "https://mon-instance.flowboard.canope.org";

// ── Reference ────────────────────────────────────────────────────────────────

const GROUPS: Group[] = [
  {
    id: "ecrans",
    title: "Écrans",
    intro: (
      <>
        Un écran correspond à une TV. Il a un <Code>publicToken</Code> : la TV affiche{" "}
        <Code>{BASE}/show/&lt;publicToken&gt;</Code>.
      </>
    ),
    endpoints: [
      {
        method: "GET",
        path: "/screens/overview",
        access: "user",
        summary: "Liste les écrans visibles par l'utilisateur, regroupés par dossier, avec leur statut.",
        response: `{
  "groups": [{ "id": 3, "name": "Accueil", "parentId": null, "screens": [/* … */], "children": [] }],
  "rootScreens": [{
    "id": 1, "name": "Hall", "folderId": null, "publicToken": "8111cee5-…",
    "itemCount": 4, "slotCount": 2,
    "activeSlot": { "templateId": 7, "endTime": "12:00" },
    "lastSeenAt": "2026-10-01T09:58:12.000Z", "online": true
  }]
}`,
      },
      {
        method: "POST",
        path: "/screens",
        access: "admin",
        summary: "Crée un écran, à la racine ou dans un dossier.",
        body: `{ "name": "Hall d'accueil", "folderId": 3 }   // folderId facultatif`,
        response: `{ "id": 12, "publicToken": "c2f0…", "slideshowPath": "/show/c2f0…" }`,
        note: "Renvoie 429 avec le code QUOTA_EXCEEDED si la limite d'écrans de votre formule est atteinte.",
      },
      {
        method: "GET",
        path: "/screens/:id",
        access: "user",
        summary: "Détail d'un écran et de ses médias.",
        response: `{
  "screen": {
    "id": 12, "folderId": null, "name": "Hall d'accueil", "publicToken": "c2f0…",
    "revision": 5, "displayMode": "QUICK", "slideshowPath": "/show/c2f0…",
    "lastSeenAt": "2026-10-01T09:58:12.000Z", "lastSeenInfo": "1920×1080 · Mozilla/5.0 …", "online": true
  },
  "items": [
    { "id": 40, "type": "IMAGE", "durationMs": 8000, "sortOrder": 0, "mimeType": "image/jpeg", "originalName": "menu.jpg" }
  ]
}`,
        note: "Un écran est « en ligne » s'il a été vu dans les 150 dernières secondes.",
      },
      {
        method: "PATCH",
        path: "/screens/:id",
        access: "admin",
        summary: "Renomme ou déplace un écran. Au moins un champ.",
        body: `{ "name": "Hall", "folderId": null, "sortOrder": 0 }`,
        response: `{ "ok": true }`,
      },
      {
        method: "DELETE",
        path: "/screens/:id",
        access: "admin",
        summary: "Supprime l'écran, ses médias et son planning.",
        response: `{ "ok": true }`,
      },
      {
        method: "POST",
        path: "/screens/:id/pair",
        access: "user",
        summary: "Associe à cet écran la TV qui affiche ce code sur la page /tv de l'instance.",
        body: `{ "code": "K7M2QX" }`,
        response: `{ "ok": true }`,
        note: "404 si le code est inconnu ou expiré (un code vit 15 minutes).",
      },
    ],
  },
  {
    id: "medias-ecran",
    title: "Médias d'un écran",
    intro: (
      <>
        Le diaporama affiché quand aucun créneau du planning n'est actif. Images (JPEG, PNG, WebP…), GIF
        et vidéos, 100 Mo maximum par fichier. Les photos trop grandes sont réduites automatiquement à la
        taille d'un écran 4K.
      </>
    ),
    endpoints: [
      {
        method: "POST",
        path: "/screens/:id/items",
        access: "user",
        summary: "Ajoute un média à la fin du diaporama (multipart/form-data, champ file).",
        query: [["durationMs", "Durée d'affichage en millisecondes (défaut 5000)."]],
        response: `{ "id": 41, "type": "IMAGE", "durationMs": 8000, "sortOrder": 1, "mimeType": "image/jpeg", "originalName": "promo.jpg" }`,
      },
      {
        method: "PATCH",
        path: "/screens/:id/items/order",
        access: "user",
        summary: "Réordonne les médias.",
        body: `{ "orderedIds": [41, 40, 42] }`,
        response: `{ "ok": true }`,
      },
      {
        method: "PATCH",
        path: "/screens/:id/items/:itemId",
        access: "user",
        summary: "Change la durée d'un média.",
        body: `{ "durationMs": 10000 }`,
        response: `{ "ok": true }`,
      },
      {
        method: "DELETE",
        path: "/screens/:id/items/:itemId",
        access: "user",
        summary: "Retire un média (le fichier est supprimé).",
        response: `{ "ok": true }`,
      },
    ],
  },
  {
    id: "planning",
    title: "Planning",
    intro: (
      <>
        Un créneau affiche un modèle sur un écran, un jour de la semaine, entre deux heures. Les heures
        sont celles du fuseau de l'instance (voir Paramètres). Un créneau dont la fin est avant le début
        passe minuit : vendredi 20:00–02:00 couvre jusqu'au samedi 2 h. Hors créneau, l'écran affiche ses
        propres médias.
      </>
    ),
    endpoints: [
      {
        method: "GET",
        path: "/screens/:id/schedule",
        access: "user",
        summary: "Créneaux de l'écran.",
        response: `{ "slots": [{ "id": 3, "screenId": 12, "dayOfWeek": 1, "startTime": "08:00", "endTime": "12:00", "templateId": 7 }] }`,
      },
      {
        method: "PUT",
        path: "/screens/:id/schedule",
        access: "admin",
        summary: "Remplace tout le planning de l'écran.",
        body: `{
  "slots": [
    { "dayOfWeek": 1, "startTime": "08:00", "endTime": "12:00", "templateId": 7 },
    { "dayOfWeek": 5, "startTime": "20:00", "endTime": "02:00", "templateId": 9 }
  ]
}`,
        response: `{ "ok": true }`,
        note: "dayOfWeek : 0 = dimanche … 6 = samedi. Heures au format HH:MM. 200 créneaux maximum. Les créneaux qui se chevauchent sont refusés (400, message en clair).",
      },
    ],
  },
  {
    id: "dossiers",
    title: "Dossiers d'écrans",
    endpoints: [
      {
        method: "GET",
        path: "/folders",
        access: "user",
        summary: "Arborescence des dossiers visibles.",
      },
      {
        method: "POST",
        path: "/folders",
        access: "admin",
        summary: "Crée un dossier (parentId = null pour la racine).",
        body: `{ "name": "Magasin de Lyon", "parentId": null }`,
      },
      {
        method: "PATCH",
        path: "/folders/:id",
        access: "admin",
        summary: "Renomme ou déplace un dossier.",
        body: `{ "name": "Lyon", "parentId": 2 }`,
        response: `{ "ok": true }`,
      },
      {
        method: "DELETE",
        path: "/folders/:id",
        access: "admin",
        summary: "Supprime le dossier, ses sous-dossiers et leurs écrans.",
        response: `{ "ok": true }`,
      },
      {
        method: "GET",
        path: "/folders/:folderId/screens",
        access: "user",
        summary: "Écrans d'un dossier.",
      },
    ],
  },
  {
    id: "modeles",
    title: "Modèles",
    intro: <>Un modèle est une composition (médias + widgets) que le planning diffuse sur un ou plusieurs écrans.</>,
    endpoints: [
      {
        method: "GET",
        path: "/template-folders/tree",
        access: "user",
        summary: "Dossiers de modèles et modèles visibles.",
        response: `{ "tree": [{ "id": 1, "name": "Promos", "parentId": null, "screens": [{ "id": 7, "name": "Soldes" }], "children": [] }],
  "rootTemplates": [{ "id": 9, "name": "Soirée" }] }`,
        note: "Dans tree, la clé screens contient les modèles du dossier.",
      },
      {
        method: "POST",
        path: "/templates",
        access: "admin",
        summary: "Crée un modèle vide, à la racine ou dans un dossier.",
        body: `{ "name": "Menu du jour", "folderId": 1 }   // folderId facultatif`,
        response: `{ "id": 10 }`,
      },
      {
        method: "GET",
        path: "/templates/:id",
        access: "user",
        summary: "Détail d'un modèle : médias et widgets.",
        response: `{
  "screen": { "id": 10, "folderId": 1, "name": "Menu du jour", "revision": 3 },
  "items": [{ "id": 55, "type": "IMAGE", "durationMs": 8000, "sortOrder": 0, "transitionType": "FADE", "transitionDurationMs": 600 }],
  "widgets": [{ "id": 4, "type": "CLOCK", "config": { "format": "time" }, "x": 0.76, "y": 0.04, "w": 0.2, "h": 0.14, "startMs": null, "endMs": null }]
}`,
        note: "Pour des raisons historiques, le modèle est renvoyé sous la clé screen.",
      },
      {
        method: "PATCH",
        path: "/templates/:id",
        access: "admin",
        summary: "Renomme ou déplace un modèle.",
        body: `{ "name": "Menu", "folderId": null }`,
        response: `{ "ok": true }`,
      },
      {
        method: "DELETE",
        path: "/templates/:id",
        access: "admin",
        summary: "Supprime le modèle et le retire des plannings.",
        response: `{ "ok": true }`,
      },
      {
        method: "POST",
        path: "/template-folders",
        access: "admin",
        summary: "Crée un dossier de modèles. PATCH et DELETE /template-folders/:id fonctionnent comme pour les dossiers d'écrans.",
        body: `{ "name": "Promos", "parentId": null }`,
      },
    ],
  },
  {
    id: "medias-modele",
    title: "Médias d'un modèle",
    endpoints: [
      {
        method: "POST",
        path: "/templates/:id/items",
        access: "user",
        summary: "Ajoute un média au modèle (multipart/form-data, champ file).",
        query: [
          ["durationMs", "Durée d'affichage en ms (défaut 5000)."],
          ["transitionType", "NONE, FADE, SLIDE_LEFT ou SLIDE_UP (défaut NONE)."],
          ["transitionDurationMs", "Durée de la transition, 50 à 5000 ms (défaut 350)."],
        ],
      },
      {
        method: "PATCH",
        path: "/templates/:id/items/order",
        access: "user",
        summary: "Réordonne les médias.",
        body: `{ "orderedIds": [56, 55] }`,
        response: `{ "ok": true }`,
      },
      {
        method: "PATCH",
        path: "/templates/:id/items/:itemId",
        access: "user",
        summary: "Change la durée ou la transition d'un média.",
        body: `{ "durationMs": 6000, "transitionType": "SLIDE_LEFT", "transitionDurationMs": 500 }`,
        response: `{ "ok": true }`,
      },
      {
        method: "DELETE",
        path: "/templates/:id/items/:itemId",
        access: "user",
        summary: "Retire un média du modèle.",
        response: `{ "ok": true }`,
      },
    ],
  },
  {
    id: "widgets",
    title: "Widgets",
    intro: (
      <>
        Un widget est posé par-dessus les médias d'un modèle. Sa position <Code>x</Code>, <Code>y</Code> et
        sa taille <Code>w</Code>, <Code>h</Code> sont des fractions de l'écran (0 à 1), donc identiques
        en 1080p et en 4K. <Code>startMs</Code> / <Code>endMs</Code> limitent son affichage à une partie de
        la boucle (null = toujours visible). Les options de <Code>config</Code> sont décrites plus bas.
      </>
    ),
    endpoints: [
      {
        method: "POST",
        path: "/templates/:id/widgets",
        access: "user",
        summary: "Ajoute un widget.",
        body: `{
  "type": "TEXT",
  "config": { "text": "Bienvenue", "color": "#ffffff", "align": "center", "size": 1 },
  "x": 0.1, "y": 0.4, "w": 0.8, "h": 0.16
}`,
        response: `{ "id": 5, "type": "TEXT", "config": { … }, "x": 0.1, "y": 0.4, "w": 0.8, "h": 0.16, "startMs": null, "endMs": null }`,
      },
      {
        method: "PATCH",
        path: "/templates/:id/widgets/:widgetId",
        access: "user",
        summary: "Modifie la position, la taille, la plage d'affichage ou la configuration.",
        body: `{ "config": { "text": "Fermé ce soir" }, "y": 0.8 }`,
        response: `{ "ok": true }`,
        note: "config remplace la configuration entière : renvoyez toutes les options.",
      },
      {
        method: "DELETE",
        path: "/templates/:id/widgets/:widgetId",
        access: "user",
        summary: "Supprime un widget.",
        response: `{ "ok": true }`,
      },
    ],
  },
  {
    id: "utilisateurs",
    title: "Utilisateurs",
    intro: (
      <>
        Un compte ADMIN voit et modifie tout. Un compte USER ne voit que les dossiers, écrans et modèles
        qui lui sont attribués.
      </>
    ),
    endpoints: [
      { method: "GET", path: "/users", access: "admin", summary: "Liste des comptes.", response: `{ "users": [{ "id": 1, "username": "admin", "role": "ADMIN", "visibleTabs": null }] }` },
      {
        method: "GET",
        path: "/users/:id/access",
        access: "admin",
        summary: "Droits d'un compte : folderIds, screenIds, templateFolderIds, templateIds, visibleTabs.",
      },
      {
        method: "POST",
        path: "/users",
        access: "admin",
        summary: "Crée un compte.",
        body: `{
  "username": "accueil",
  "password": "…",
  "role": "USER",
  "folderIds": [3],
  "screenIds": [],
  "templateFolderIds": [],
  "templateIds": [7]
}`,
        response: `{ "id": 4 }`,
      },
      {
        method: "PATCH",
        path: "/users/:id",
        access: "admin",
        summary: "Change le mot de passe, le rôle ou les droits (mêmes champs, tous facultatifs).",
        response: `{ "ok": true }`,
      },
      { method: "DELETE", path: "/users/:id", access: "admin", summary: "Supprime un compte.", response: `{ "ok": true }` },
    ],
  },
  {
    id: "parametres",
    title: "Paramètres de l'instance",
    endpoints: [
      { method: "GET", path: "/instance/settings", access: "user", summary: "Fuseau horaire de l'instance.", response: `{ "timezone": "Europe/Paris" }` },
      {
        method: "PATCH",
        path: "/instance/settings",
        access: "admin",
        summary: "Change le fuseau horaire (nom IANA). Le planning est évalué dans ce fuseau.",
        body: `{ "timezone": "America/Montreal" }`,
        response: `{ "ok": true, "timezone": "America/Montreal" }`,
      },
      {
        method: "GET",
        path: "/instance/plan",
        access: "admin",
        summary: "Formule, limites et consommation actuelle.",
        response: `{ "planId": "FREE", "limits": { … }, "usage": { … } }`,
      },
    ],
  },
  {
    id: "lecteur",
    title: "Lecteur (TV)",
    intro: (
      <>
        Endpoints utilisés par la page <Code>/show/&lt;publicToken&gt;</Code>. Ils ne demandent pas de
        connexion : le <Code>publicToken</Code> sert de clé. Utiles pour écrire votre propre lecteur.
      </>
    ),
    endpoints: [
      {
        method: "GET",
        path: "/public/screens/:token/manifest",
        access: "public",
        summary: "Ce que l'écran doit afficher maintenant.",
        response: `{
  "revision": 5,
  "contentKey": "5:slot3:tpl7r2",
  "screenId": 12,
  "timezone": "Europe/Paris",
  "items": [{ "id": 55, "type": "IMAGE", "durationMs": 8000, "mimeType": "image/jpeg",
              "url": "/api/public/templates/7/media/55", "transitionType": "FADE", "transitionDurationMs": 600 }],
  "widgets": [{ "id": 4, "type": "CLOCK", "config": { "format": "time" }, "x": 0.76, "y": 0.04, "w": 0.2, "h": 0.14 }]
}`,
        note: "Interrogez-le toutes les 30 à 60 s et rechargez le contenu quand contentKey change (modification ou changement de créneau).",
      },
      {
        method: "GET",
        path: "/public/screens/:token/media/:itemId",
        access: "public",
        summary: "Fichier d'un média de l'écran (gère les requêtes Range pour les vidéos). Les médias de modèle suivent l'URL donnée dans le manifeste.",
      },
      {
        method: "POST",
        path: "/public/screens/:token/ping",
        access: "public",
        summary: "Signale que la TV est allumée (toutes les 60 s). Alimente le statut en ligne.",
        body: `{ "w": 1920, "h": 1080 }`,
        response: `{ "ok": true }`,
      },
      {
        method: "POST",
        path: "/public/pair",
        access: "public",
        summary: "Demande un code d'association à afficher sur la TV.",
        response: `{ "code": "K7M2QX", "expiresInSec": 900 }`,
      },
      {
        method: "GET",
        path: "/public/pair/:code",
        access: "public",
        summary: "État du code : waiting, paired (avec le token de l'écran) ou 404 si expiré.",
        response: `{ "status": "paired", "token": "c2f0…", "screenName": "Hall d'accueil" }`,
      },
    ],
  },
  {
    id: "meteo",
    title: "Météo",
    endpoints: [
      {
        method: "GET",
        path: "/public/weather",
        access: "public",
        summary: "Météo actuelle à ces coordonnées (données MET Norway, mises en cache 5 min).",
        query: [
          ["lat", "Latitude, -90 à 90."],
          ["lon", "Longitude, -180 à 180."],
        ],
      },
      {
        method: "GET",
        path: "/geocode/cities",
        access: "user",
        summary: "Recherche de communes françaises (geo.api.gouv.fr) pour configurer le widget Météo.",
        query: [["q", "Début du nom de la commune."]],
      },
    ],
  },
];

const WIDGET_CONFIGS: Array<{ type: string; label: string; options: string }> = [
  { type: "TEXT", label: "Texte", options: "text, color (#rrggbb), align (left | center | right), size (multiplicateur, 1 = normal), bold (booléen), fontFamily, background (couleur ou null)" },
  { type: "CLOCK", label: "Horloge", options: "format (time | time-seconds | date-time | date), color, background (booléen)" },
  { type: "WEATHER_CURRENT", label: "Météo", options: "lat, lon, city (nom affiché), units (celsius | fahrenheit), showCity (booléen)" },
  { type: "TICKER", label: "Bandeau défilant", options: "text, speed (slow | normal | fast), color, background" },
  { type: "QRCODE", label: "QR code", options: "url, label (texte sous le code)" },
];

const ERRORS: Array<[string, string]> = [
  ["400", "Requête invalide : champ manquant ou mal formé. Le message indique lequel."],
  ["401", "Pas de session, ou jeton expiré : reconnectez-vous."],
  ["403", "Le compte n'a pas accès à cette ressource, ou l'action est réservée aux administrateurs."],
  ["404", "Ressource introuvable."],
  ["429", "Trop de requêtes, ou limite de la formule atteinte (code QUOTA_EXCEEDED)."],
  ["503", "Service tiers indisponible (météo, recherche de communes)."],
];

// ── UI ───────────────────────────────────────────────────────────────────────

function Code({ children }: { children: React.ReactNode }) {
  return <code className="rounded bg-gray-100 px-1.5 py-0.5 font-mono text-[0.85em] text-gray-800">{children}</code>;
}

function CodeBlock({ code, label }: { code: string; label?: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="group relative mt-2">
      {label && <p className="mb-1 text-xs font-medium uppercase tracking-wider text-gray-400">{label}</p>}
      <pre className="overflow-x-auto rounded-lg bg-gray-900 p-4 text-[13px] leading-relaxed text-gray-100">
        <code>{code}</code>
      </pre>
      <button
        type="button"
        onClick={() => {
          void navigator.clipboard?.writeText(code).then(() => {
            setCopied(true);
            window.setTimeout(() => setCopied(false), 1500);
          });
        }}
        className="absolute right-2 top-2 rounded-md bg-white/10 p-1.5 text-gray-300 opacity-0 transition-opacity hover:bg-white/20 group-hover:opacity-100"
        style={label ? { top: "1.9rem" } : undefined}
        aria-label="Copier"
      >
        {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
      </button>
    </div>
  );
}

function EndpointCard({ e }: { e: Endpoint }) {
  return (
    <div className="rounded-xl border border-gray-200 p-4">
      <div className="flex flex-wrap items-center gap-2">
        <span className={`rounded-md px-2 py-0.5 font-mono text-xs font-bold ring-1 ring-inset ${METHOD_STYLE[e.method]}`}>
          {e.method}
        </span>
        <code className="break-all font-mono text-sm font-semibold text-gray-900">/api{e.path}</code>
        <span className={`ml-auto rounded-full px-2 py-0.5 text-xs font-medium ${ACCESS_STYLE[e.access]}`}>
          {ACCESS_LABEL[e.access]}
        </span>
      </div>
      <p className="mt-2 text-sm text-gray-600">{e.summary}</p>
      {e.query && (
        <div className="mt-3">
          <p className="mb-1 text-xs font-medium uppercase tracking-wider text-gray-400">Paramètres d'URL</p>
          <ul className="space-y-1 text-sm text-gray-600">
            {e.query.map(([name, desc]) => (
              <li key={name}>
                <Code>{name}</Code> — {desc}
              </li>
            ))}
          </ul>
        </div>
      )}
      {e.body && <CodeBlock label="Corps JSON" code={e.body} />}
      {e.response && <CodeBlock label="Réponse" code={e.response} />}
      {e.note && <p className="mt-3 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-800">{e.note}</p>}
    </div>
  );
}

function H2({ id, children }: { id: string; children: React.ReactNode }) {
  return (
    <h2 id={`api-${id}`} className="mb-3 mt-12 scroll-mt-20 text-xl font-bold text-gray-900">
      {children}
    </h2>
  );
}

const QUICKSTART = `# 1. Se connecter (le cookie de session est enregistré dans cookies.txt)
curl -c cookies.txt -H "Content-Type: application/json" \\
  -d '{"username":"admin","password":"VOTRE_MOT_DE_PASSE"}' \\
  ${BASE}/api/auth/login

# 2. Créer un écran
curl -b cookies.txt -H "Content-Type: application/json" \\
  -d '{"name":"Hall d'"'"'accueil"}' \\
  ${BASE}/api/screens
# → { "id": 12, "publicToken": "c2f0…", "slideshowPath": "/show/c2f0…" }

# 3. Ajouter une image affichée 8 secondes
curl -b cookies.txt -F "file=@menu.jpg" \\
  "${BASE}/api/screens/12/items?durationMs=8000"

# 4. Ouvrir ${BASE}/show/c2f0… sur la TV`;

export function ApiDocs() {
  return (
    <div className="max-w-3xl pb-24">
      <h1 className="mb-2 text-2xl font-bold text-gray-900">API</h1>
      <p className="leading-relaxed text-gray-600">
        Chaque instance Flowboard expose l'API REST qu'utilise l'application elle-même. Vous pouvez
        l'appeler depuis vos scripts pour créer des écrans, envoyer des médias ou programmer des
        diffusions automatiquement.
      </p>

      <nav className="mt-6 rounded-xl border border-gray-200 bg-gray-50 p-4 text-sm">
        <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-gray-400">Sommaire</p>
        <ul className="grid gap-1 sm:grid-cols-2">
          {[
            ["bases", "Bases"],
            ["auth", "Authentification"],
            ["erreurs", "Erreurs et limites"],
            ["demarrage", "Démarrage rapide"],
            ...GROUPS.map((g) => [g.id, g.title]),
            ["config-widgets", "Options des widgets"],
          ].map(([id, title]) => (
            <li key={id}>
              <a href={`#api-${id}`} className="text-indigo-600 hover:underline">
                {title}
              </a>
            </li>
          ))}
        </ul>
      </nav>

      <H2 id="bases">Bases</H2>
      <ul className="list-disc space-y-1.5 pl-5 text-sm leading-relaxed text-gray-600">
        <li>
          URL de base : <Code>https://&lt;votre-instance&gt;.flowboard.canope.org/api</Code>, celle de votre
          espace suivie de <Code>/api</Code>.
        </li>
        <li>
          Les requêtes et les réponses sont en JSON (<Code>Content-Type: application/json</Code>), sauf
          l'envoi de fichiers en <Code>multipart/form-data</Code>.
        </li>
        <li>Les identifiants sont des entiers. Les dates sont en ISO 8601 (UTC).</li>
        <li>
          Les badges indiquent qui peut appeler l'endpoint : <b>Public</b> (sans connexion),{" "}
          <b>Connecté</b> (tout compte ayant accès à la ressource) ou <b>Admin</b>.
        </li>
      </ul>

      <H2 id="auth">Authentification</H2>
      <p className="text-sm leading-relaxed text-gray-600">
        Connectez-vous avec un compte de votre instance. La réponse dépose un cookie{" "}
        <Code>flowboard_token</Code> valable 7 jours, à renvoyer à chaque requête. Vous pouvez aussi
        envoyer sa valeur dans l'en-tête <Code>Authorization: Bearer &lt;jeton&gt;</Code>.
      </p>
      <CodeBlock
        code={`POST /api/auth/login
{ "username": "admin", "password": "…" }

→ 200 { "ok": true, "user": { "id": 1, "username": "admin", "role": "ADMIN" } }
   Set-Cookie: flowboard_token=eyJhbGciOi…; HttpOnly; Secure; SameSite=Lax`}
      />
      <p className="mt-3 text-sm leading-relaxed text-gray-600">
        <Code>GET /api/auth/me</Code> renvoie le compte connecté, <Code>POST /api/auth/logout</Code> efface
        le cookie. Pour un script, créez un compte dédié avec le rôle USER limité aux écrans concernés :
        si son mot de passe fuit, le reste de l'instance reste protégé.
      </p>

      <H2 id="erreurs">Erreurs et limites</H2>
      <p className="text-sm leading-relaxed text-gray-600">
        En cas d'erreur, la réponse contient <Code>{`{ "error": "message" }`}</Code>.
      </p>
      <table className="mt-3 w-full text-left text-sm">
        <tbody className="divide-y divide-gray-100">
          {ERRORS.map(([code, desc]) => (
            <tr key={code}>
              <td className="w-16 py-2 align-top font-mono font-semibold text-gray-900">{code}</td>
              <td className="py-2 text-gray-600">{desc}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="mt-3 text-sm leading-relaxed text-gray-600">
        Limites par adresse IP : 200 requêtes par minute sur l'API, 600 sur les endpoints publics du lecteur, 10
        tentatives de connexion par minute. Fichiers : 100 Mo maximum.
      </p>

      <H2 id="demarrage">Démarrage rapide</H2>
      <p className="text-sm leading-relaxed text-gray-600">Un écran qui diffuse une image, en quatre commandes :</p>
      <CodeBlock code={QUICKSTART} />

      {GROUPS.map((g) => (
        <section key={g.id}>
          <H2 id={g.id}>{g.title}</H2>
          {g.intro && <p className="mb-4 text-sm leading-relaxed text-gray-600">{g.intro}</p>}
          <div className="space-y-3">
            {g.endpoints.map((e) => (
              <EndpointCard key={`${e.method} ${e.path}`} e={e} />
            ))}
          </div>
        </section>
      ))}

      <H2 id="config-widgets">Options des widgets</H2>
      <p className="mb-3 text-sm leading-relaxed text-gray-600">
        Toutes les options sont facultatives ; une option absente prend sa valeur par défaut.
      </p>
      <table className="w-full text-left text-sm">
        <thead>
          <tr className="border-b border-gray-200 text-xs uppercase tracking-wider text-gray-400">
            <th className="py-2 pr-4 font-medium">type</th>
            <th className="py-2 font-medium">config</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {WIDGET_CONFIGS.map((w) => (
            <tr key={w.type}>
              <td className="py-2 pr-4 align-top">
                <Code>{w.type}</Code>
                <span className="mt-1 block text-xs text-gray-400">{w.label}</span>
              </td>
              <td className="py-2 text-gray-600">{w.options}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
